import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import moment from "moment";
import { FaRegStar, FaStar } from "react-icons/fa";
import { AddNewTalentColoredIcon, SearchColoredIcon } from "../assets/icons";
import Button from "../components/Button";
import voiceStore from "../stores/voiceStore";
import { PiWarningCircleFill } from "react-icons/pi";
import generalStore from "../stores/generalStore";
import { FaWhatsapp } from "react-icons/fa";

const Talents = observer(() => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [talentToDelete, setTalentToDelete] = useState(null);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTalentId, setExpandedTalentId] = useState(null);

  useEffect(() => {
    voiceStore.getTalents();
  }, []);

  const calculateAge = (date) => {
    const birthDate = moment(date);
    const today = moment();
    const age = today.diff(birthDate, "years");
    return isNaN(age) ? "NA" : age;
  };

  const toggleDeleteModal = (talent = null) => {
    setShowDeleteModal(!showDeleteModal);
    setTalentToDelete(talent);
  };

  const handleDelete = () => {
    if (talentToDelete) {
      voiceStore.deleteTalent(talentToDelete.TalentId);
      toggleDeleteModal();
    }
  };

  const formatData = (value) => {
    if (!value || value === "null" || value === "undefined" || value === '""') {
      return "NA";
    }
    return value;
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredTalents = () => {
    if (!searchQuery) {
      return voiceStore.talents;
    }

    const query = searchQuery.toLowerCase();

    return voiceStore.talents.filter((talent) => {
      const talentName = talent.TalentName?.toLowerCase() || "";
      const email = talent.Email?.toLowerCase() || "";
      const mobile = talent.MobileNo?.toLowerCase() || "";

      return (
        talentName.includes(query) ||
        email.includes(query) ||
        mobile.includes(query)
      );
    });
  };

  const talents = filteredTalents();

  const toggleAccordion = (talentId) => {
    setExpandedTalentId(expandedTalentId === talentId ? null : talentId);
  };

  return (
    <div className="bg-[#F2F4F8] h-screen">
      <div className="flex justify-end mx-6 py-3 gap-3 lg:gap-6">
        <Button
          className="hidden md:flex rounded-md"
          onClick={() => voiceStore.changeStepperVisible(true)}
          icon={AddNewTalentColoredIcon}
          title="Add New"
        />
        <Button
          onClick={() => voiceStore.changeStepperVisible(true)}
          className="flex md:hidden rounded-md"
          icon={AddNewTalentColoredIcon}
        />
        <Button
          className="hidden md:flex rounded-md"
          icon={SearchColoredIcon}
          title="Search"
          onClick={() => setShowSearchInput(!showSearchInput)}
        />
        <Button
          className="flex md:hidden rounded-md"
          icon={SearchColoredIcon}
          onClick={() => setShowSearchInput(!showSearchInput)}
        />
      </div>

      <div className="flex flex-1 h-full bg-white lg:mx-6">
        <div className="w-full h-full p-5 box-border">
          <div className="flex justify-between items-center py-3 lg:py-6 gap-1">
            <div className="flex items-center gap-1">
              <span className="font-medium text-base text-black">Talents</span>
              <span className="font-normal text-xs text-white py-1 px-2 rounded-full lg:rounded-xl bg-[#F89A1F]">
                {talents.length}
              </span>
            </div>
            {showSearchInput && (
              <input
                type="text"
                className="border border-black/60 placeholder-black/80 rounded-md px-3 py-2 max-w-48 lg:w-full lg:max-w-lg"
                placeholder="Search talents..."
                value={searchQuery}
                onChange={handleSearchInputChange}
              />
            )}
          </div>

          <div className="flex flex-col gap-4 pb-20">
            {talents.length > 0 ? (
              talents.map((talent) => (
                <div
                  key={talent.TalentId}
                  className="border border-[#DDE1E6] rounded-md"
                >
                  <div
                    className="flex justify-between items-center p-4 cursor-pointer hover:bg-black/[0.03]"
                    onClick={() => toggleAccordion(talent.TalentId)}
                  >
                    <div className="flex items-center gap-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          voiceStore.setFavoriteTalent(talent.TalentId);
                        }}
                      >
                        {talent?.IsFavorite === "1" ? (
                          <FaStar color="#F89A1F" size={15} />
                        ) : (
                          <FaRegStar color="#F89A1F" size={15} />
                        )}
                      </button>
                      <span className="font-normal text-sm text-[#697077]">
                        {expandedTalentId === talent.TalentId ? "-" : "+"}
                      </span>
                      <span>
                        {" "}
                        {talent?.ProfileImgPath ? (
                          <img
                            src={`${process.env.REACT_APP_BASE_URL}/${talent.ProfileImgPath}`}
                            className="w-10 h-10 rounded-full"
                            alt={talent?.TalentName}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex justify-center items-center text-sm text-black/50">
                            {generalStore.getInitials(talent?.TalentName)}
                          </div>
                        )}
                      </span>
                      <span className="font-medium text-sm text-[#21272A]">
                        {talent?.TalentName}
                      </span>
                      {talent?.MobileNo && (
                        <div className="flex items-center gap-2">
                          <span className="font-normal text-sm text-[#697077]">
                            <strong>Phone: </strong>
                            {formatData(talent?.MobileNo)}
                          </span>
                          <a
                            href={`https://wa.me/${talent?.MobileNo}`}
                            target="_blank"
                          >
                            <FaWhatsapp className="text-[#25D366]" />
                          </a>
                        </div>
                      )}
                      {talent?.Email && (
                        <span className="font-normal text-sm text-[#697077]">
                          <strong>Email: </strong>
                          <a
                            href={`mailto:${talent?.Email}`}
                            className="text-blue-500 underline"
                          >
                            {formatData(talent?.Email)}
                          </a>
                        </span>
                      )}
                    </div>
                    {/* Delete Button */}
                    <button
                      onClick={() => toggleDeleteModal(talent)}
                      className="text-red-500"
                    >
                      Delete Talent
                    </button>
                  </div>
                  {expandedTalentId === talent.TalentId && (
                    <div className="p-4 bg-[#F9FAFB]">
                      <p>
                        <strong>Age:</strong>{" "}
                        {formatData(
                          talent?.Age ? calculateAge(talent.Age) : talent?.Age
                        )}
                      </p>
                      <p>
                        <strong>Accents:</strong> {formatData(talent?.Accents)}
                      </p>
                      <p>
                        <strong>Social Accounts:</strong>{" "}
                        {formatData(talent?.SocialAccounts)}
                      </p>
                      <p>
                        <strong>Nationality:</strong>{" "}
                        {formatData(talent?.Nationality)}
                      </p>
                      <p>
                        <strong>Notes:</strong> {formatData(talent?.Notes)}
                      </p>
                      {/* View Talent Profile Button */}
                      <div className="flex gap-4 mt-4">
                        <button
                          onClick={() =>
                            voiceStore.getTalentById(talent.TalentId)
                          }
                          className="text-[#F89A1F]"
                        >
                          View More
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-5">No talents found.</div>
            )}
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white rounded-lg p-5 w-[22rem]">
            <div className="flex items-center gap-x-2 mb-4">
              <PiWarningCircleFill color="#F89A1F" />
              <span className="font-normal text-sm">
                Are you sure you want to delete this talent?
              </span>
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => toggleDeleteModal()}
                className="border border-[#D9D9D9] bg-white rounded-sm"
              >
                <span className="font-normal text-sm px-3">No</span>
              </button>
              <button
                onClick={handleDelete}
                className="border border-black bg-black rounded-sm"
              >
                <span className="font-normal text-sm px-3 text-white">Yes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default Talents;
